import type {
  ConfigurationType,
  LevelType,
  SizingCalculatorAttributesType,
  SpaceType
} from 'data/types';
import type { ProjectState } from 'features/project/project-slice';
import type { StructureState } from 'features/structure/structure-slice';
import { truncateAttributes } from 'utils/attributes';
import { truncateConfigurations } from 'utils/configurations';

function getNumberOfWindows(
  sizingAttributes: SizingCalculatorAttributesType | undefined
): number {
  if (!sizingAttributes) return 0;
  const fromAboveGrade = (sizingAttributes.aboveGradeWalls ?? []).reduce(
    (acc, w) => acc + (w.verticalGlass?.length ?? 0),
    0
  );
  const fromBelowGrade = (sizingAttributes.belowGradeWalls ?? []).reduce(
    (acc, w) => acc + (w.verticalGlass?.length ?? 0),
    0
  );
  return fromAboveGrade + fromBelowGrade;
}

function transformSpace(space: SpaceType) {
  const { sizingAttributes, ...rest } = space;
  return {
    ...rest,
    attributes: truncateAttributes(space.attributes ?? []),
    numberOfWindows: getNumberOfWindows(sizingAttributes)
  };
}

function structureWithTruncatedAttributes(levels: LevelType[]) {
  return levels.map((level) => {
    const { sections, ...levelRest } = level;
    return {
      ...levelRest,
      attributes: truncateAttributes(level.attributes ?? []),
      spaces: sections.flatMap((section) =>
        section.spaces.map((space) => transformSpace(space))
      )
    };
  });
}

export function buildDebugExport(
  project: ProjectState,
  structure: StructureState,
  configurations: { configurations: ConfigurationType[] }
) {
  return {
    project: {
      projectDetails: project.projectDetails,
      projectSettings: truncateAttributes(project.projectSettings ?? [])
    },
    property: {
      propertyLocation: project.propertyLocation,
      locationData: project.locationData,
      attomData: project.attomData
    },
    structure: {
      structure: structureWithTruncatedAttributes(structure.structure),
      existingSystems: structure.existingSystems,
      structureTypeId: structure.structureTypeId,
      structureSubtypeId: structure.structureSubtypeId,
      conditionedLevels: structure.conditionedLevels,
      notes: structure.notes
    },
    configurations: truncateConfigurations(configurations.configurations)
  };
}
